import { Reoverlay } from "reoverlay";
import AddToHomeScreenModal from "../../components/AddToHomeScreenModal/AddToHomeScreenModal";
import {
  isA2hsPromptDevice,
  isRunningAsInstalledPwa,
} from "./a2hsDetection";
import {
  loadA2hsPromptState,
  markA2hsPromptPending,
  markA2hsPromptShown,
  setA2hsPromptStatus,
} from "./a2hsStorage";

/** First lesson never prompts; from lesson 2 onward we may prompt. */
const MIN_LESSON_ORDINAL_FOR_A2HS = 2;
const A2HS_HOME_PROMPT_DELAY_MS = 500;

const canShowA2hsPromptUi = (): boolean => {
  if (!isA2hsPromptDevice()) return false;
  if (isRunningAsInstalledPwa()) return false;

  const { status } = loadA2hsPromptState();
  return status === "never_shown";
};

const canQueueA2hsPrompt = (): boolean => {
  if (!isA2hsPromptDevice()) return false;
  if (isRunningAsInstalledPwa()) return false;

  const { status } = loadA2hsPromptState();
  return status !== "accepted" && status !== "installed";
};

/**
 * After any passed lesson from #2 onward (2, 3, 5, …), queue the prompt
 * until the user accepts or the PWA is installed.
 */
export const shouldMarkA2hsPromptPending = (options: {
  overallQuizOrdinal: number | null;
  isPassed: boolean;
}): boolean => {
  if (!options.isPassed) return false;
  if (
    options.overallQuizOrdinal == null ||
    options.overallQuizOrdinal < MIN_LESSON_ORDINAL_FOR_A2HS
  ) {
    return false;
  }
  return canQueueA2hsPrompt();
};

export const shouldShowPendingA2hsPrompt = (): boolean => {
  if (!canShowA2hsPromptUi()) return false;
  return loadA2hsPromptState().pending === true;
};

export const syncA2hsInstalledFromDisplayMode = (): void => {
  if (!isRunningAsInstalledPwa()) return;

  const { status } = loadA2hsPromptState();
  if (status !== "installed") {
    setA2hsPromptStatus("installed");
  }
};

/** Call from quiz final screen after a passed lesson. */
export const markA2hsPromptPendingAfterEligibleLesson = (options: {
  overallQuizOrdinal: number | null;
  isPassed: boolean;
}): void => {
  if (!shouldMarkA2hsPromptPending(options)) return;
  markA2hsPromptPending();
};

const showA2hsModalOnce = (): void => {
  if (!canShowA2hsPromptUi()) return;
  if (loadA2hsPromptState().pending !== true) return;

  markA2hsPromptShown();
  Reoverlay.showModal(AddToHomeScreenModal);
};

/** Show the deferred prompt when the user lands back on Home after a quiz. */
export const maybeShowPendingA2hsPrompt = (): (() => void) | undefined => {
  if (!shouldShowPendingA2hsPrompt()) return undefined;

  let cancelled = false;
  const timeoutId = window.setTimeout(() => {
    if (cancelled) return;
    showA2hsModalOnce();
  }, A2HS_HOME_PROMPT_DELAY_MS);

  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
  };
};

/** Open the install / add-to-home-screen flow from Settings (always available). */
export const openA2hsPromptFromSettings = (): boolean => {
  if (isRunningAsInstalledPwa()) {
    setA2hsPromptStatus("installed");
    return false;
  }

  markA2hsPromptShown();
  Reoverlay.showModal(AddToHomeScreenModal);
  return true;
};
